import { useState, useCallback } from 'react';
import shareService, { ExerciseShareData, RoutineShareData } from '../services/share.service';
import logger from '../utils/logger';

const shareLogger = logger.module('useShare');

interface UseShareReturn {
  isSharing: boolean;
  shareExercise: (exerciseData: ExerciseShareData) => Promise<boolean>;
  shareRoutine: (routineData: RoutineShareData) => Promise<boolean>;
  shareText: (title: string, text: string, url?: string) => Promise<boolean>;
  isWebShareSupported: boolean;
  canShareFiles: boolean;
}

/**
 * Hook for handling sharing functionality
 */
export const useShare = (): UseShareReturn => {
  const [isSharing, setIsSharing] = useState(false);

  const shareExercise = useCallback(async (exerciseData: ExerciseShareData): Promise<boolean> => {
    if (isSharing) return false;

    setIsSharing(true);
    try {
      const success = await shareService.shareExercise(exerciseData);
      shareLogger.info('Exercise share completed', { success, exerciseId: exerciseData.exerciseId });
      return success;
    } catch (error) {
      shareLogger.error('Exercise share failed:', error);
      return false;
    } finally {
      setIsSharing(false);
    }
  }, [isSharing]);

  const shareRoutine = useCallback(async (routineData: RoutineShareData): Promise<boolean> => {
    if (isSharing) return false;

    setIsSharing(true);
    try {
      const success = await shareService.shareRoutine(routineData);
      shareLogger.info('Routine share completed', { success, routineKey: routineData.routineKey });
      return success;
    } catch (error) {
      shareLogger.error('Routine share failed:', error);
      return false;
    } finally {
      setIsSharing(false);
    }
  }, [isSharing]);

  const shareText = useCallback(async (title: string, text: string, url?: string): Promise<boolean> => {
    if (isSharing) return false;

    setIsSharing(true);
    try {
      const success = await shareService.share({
        title,
        text,
        url
      });
      shareLogger.info('Text share completed', { success, title });
      return success;
    } catch (error) {
      shareLogger.error('Text share failed:', error);
      return false;
    } finally {
      setIsSharing(false);
    }
  }, [isSharing]);

  return {
    isSharing,
    shareExercise,
    shareRoutine,
    shareText,
    isWebShareSupported: shareService.isWebShareSupported(),
    canShareFiles: shareService.canShareFiles()
  };
};

export default useShare;